/**
 * Pause background enrichment during active grid interaction.
 */
const DEFAULT_PAUSE_MS = 4000;

let enrichmentPausedUntil = 0;
let listenersInstalled = false;

export function pauseEnrichmentDuringInteraction(ms = DEFAULT_PAUSE_MS): void {
  enrichmentPausedUntil = Date.now() + ms;
}

export function isEnrichmentPausedByInteraction(nowMs = Date.now()): boolean {
  return nowMs < enrichmentPausedUntil;
}

export async function waitForEnrichmentSlot(
  isCancelled?: () => boolean,
  pollMs = 120,
): Promise<boolean> {
  while (isEnrichmentPausedByInteraction()) {
    if (isCancelled?.()) return false;
    await new Promise((resolve) => setTimeout(resolve, pollMs));
  }
  return !isCancelled?.();
}

export function installEnrichmentInteractionPause(): () => void {
  if (listenersInstalled || typeof window === 'undefined') return () => {};
  listenersInstalled = true;
  const onInteract = () => pauseEnrichmentDuringInteraction();
  const types = ['pointerdown', 'wheel', 'touchstart', 'keydown', 'scroll'] as const;
  for (const type of types) {
    window.addEventListener(type, onInteract, { passive: true, capture: true });
  }
  return () => {
    listenersInstalled = false;
    for (const type of types) {
      window.removeEventListener(type, onInteract, { capture: true });
    }
  };
}

export function resetEnrichmentInteractionPauseForTests(): void {
  enrichmentPausedUntil = 0;
  listenersInstalled = false;
}

export function deferToIdle(callback: () => void, timeoutMs = 8000): () => void {
  if (typeof window === 'undefined') {
    callback();
    return () => {};
  }
  if (typeof window.requestIdleCallback === 'function') {
    const id = window.requestIdleCallback(callback, { timeout: timeoutMs });
    return () => {
      if (typeof window.cancelIdleCallback === 'function') window.cancelIdleCallback(id);
    };
  }
  const timer = window.setTimeout(callback, Math.min(timeoutMs, 2500));
  return () => window.clearTimeout(timer);
}

export async function idleYield(timeoutMs = 5000): Promise<void> {
  await new Promise<void>((resolve) => {
    deferToIdle(() => resolve(), timeoutMs);
  });
}
